import {
  type PositionFilter,
  type StateResponse,
  type TeamConferenceFilter,
} from '@on3/api';
import { Filter } from '@on3/ui-lib/components/Filter/Filter';
import { useSite } from '@on3/ui-lib/index';
import { formatFilterOptions } from '@on3/ui-lib/utils/formatters';
import { useDynamicRoute } from '@on3/ui-lib/utils/hooks/useDynamicRoute';
import FilterTeams from 'components/Db/Filters/FilterTeams';
import { Nil100 } from 'components/Svg/Nil100';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useMemo } from 'react';

import styles from './NilNavBar.module.scss';

interface INilNavBarProps {
  teams?: TeamConferenceFilter[];
  positions?: PositionFilter[];
  states?: StateResponse[];
  showSports?: boolean;
}
export const NilNavBar = ({
  teams,
  positions,
  showSports,
  states,
}: INilNavBarProps) => {
  const { sportsData } = useSite();
  const { formatHref } = useDynamicRoute();
  const router = useRouter();
  const { asPath, query, pathname } = router;
  const sportIsRequired = pathname.includes('[sport]');
  const {
    sport = 'football',
    position,
    state,
    'team-key': teamKey,
    'transfer-portal': transferPortal,
    organizationType,
  } = query;

  const filterSports =
    !!showSports &&
    !!sportsData &&
    formatFilterOptions({
      data: sportsData,
      labelKey: 'name',
      valueKey: 'name',
      defaultOption: sportIsRequired ? undefined : 'Sports',
    });
  const filterPositions =
    !!positions &&
    formatFilterOptions({
      data: positions,
      labelKey: 'name',
      valueKey: 'abbreviation',
      defaultOption: 'Positions',
    });
  const filterStates =
    !!states &&
    formatFilterOptions({
      data: states,
      labelKey: 'name',
      valueKey: 'abbreviation',
      defaultOption: 'States',
    });
  const isHighSchool = organizationType === 'high-school';
  const showStates = !!filterStates && isHighSchool;

  const dataType = useMemo(() => {
    if (asPath.includes('nil-valuations')) {
      return 'nil100';
    }

    if (transferPortal) {
      return 'transfer-portal';
    }

    return organizationType;
  }, [asPath, organizationType, transferPortal]);

  return (
    <nav className={styles.block} data-page-type={dataType}>
      <div className={styles.container}>
        <Link data-type="nil100" href={`/nil/rankings/player/nil-valuations/`}>
          <Nil100 fill={dataType === 'nil100' ? '#fe3b1f' : '#6F747B'} />
        </Link>
        {/* TODO:Use Format href to add position if its in the router query */}
        {/* HIGHSCHOOL WILL BE THE ONLY ONE USING STATE */}
        <Link
          aria-label="High school"
          data-type="high-school"
          href={`/nil/rankings/player/high-school/${sport}/`}
        >
          High School
        </Link>

        <Link
          data-type="college"
          href={`/nil/rankings/player/college/${sport}/`}
        >
          College
        </Link>
        <Link
          data-type="transfer-portal"
          href={`/nil/rankings/player/college/${sport}/?transfer-portal=true`}
        >
          Transfer Portal
        </Link>
      </div>
      <div className={styles.filters}>
        {!isHighSchool && (
          <FilterTeams
            name="team-key"
            selected={teamKey?.toString()}
            showClear={!!teamKey}
            teams={teams}
            useOrgKey
          />
        )}

        {!!filterSports && (
          <Filter
            name="sport"
            navigate={(_, v) =>
              formatHref(
                sportIsRequired
                  ? { params: { sport: v }, query: { position: undefined } }
                  : { query: { sport: v === 'Sports' ? undefined : v } },
              )
            }
            options={filterSports}
            value={String(sport)}
          />
        )}
        {!!filterPositions && (
          <Filter
            name="position"
            navigate={(_, v) =>
              formatHref({
                query: {
                  position: v === 'Positions' ? undefined : v,
                },
              })
            }
            options={filterPositions}
            value={String(position)}
          />
        )}
        {showStates && (
          <Filter
            name="state"
            navigate={(_, v) =>
              formatHref({
                query: {
                  state: v === 'States' ? undefined : v,
                },
              })
            }
            options={filterStates}
            value={String(state)}
          />
        )}
      </div>
    </nav>
  );
};
